/* ─────────────────────────────────────────────────────────────
   INTRINSIC VALUE TAB — Forecast Scenario Builder

   Replicates the Scenario Builder artifact against live production data:

     GET  /stocks/{ticker}/intrinsic-value           header cards, consensus-anchor
                                                     note, FCFE series, sensitivity
                                                     grid, Data-as-of, 3 statements
     POST /stocks/{ticker}/intrinsic-value/scenario   assumptions panel (what-if)
     POST/GET/DELETE .../intrinsic-value/scenarios    save-version bar
     POST .../intrinsic-value/scenario/export         Excel download
     GET  /stocks/{ticker}/valuation-history          value over time

   Section order follows the artifact: header cards → consensus-anchor note →
   engine internals → warnings → assumptions panel → sensitivity grid →
   FCFE historical & projected → value over time → statements.

   UNIT CONTRACT — the field names do not tell you which is which, so:
     DECIMALS (×100 to display): discount_rate, terminal_growth_rate,
       tv_share_of_value, tv_share_before_continuation, growth_rate_used,
       cap_ratio, roe5, fy1_rem_frac
     ALREADY PERCENTAGES (never multiply): margin_of_safety_pct,
       upside_downside_pct

   The fair-value builder and fair-value/analyst-estimate history live on the
   Fair Value tab; the factor-ratings history lives on the FA Ratings tab.
───────────────────────────────────────────────────────────── */

import React, { useEffect, useState } from "react";
import { Tooltip as MuiTooltip } from "@mui/material";
import { getIntrinsicValue, computeIntrinsicValueScenario } from "../../../api/stocks_api";
import { formatCurrency, fmtL } from "../../../utils/currency";
import { STATEMENTS } from "../../../config/statements";
import {
  engineMeta,
  isFallbackMethod,
  isBankEngine,
  isAssetManagerEngine,
  nbMethodLabel,
  confidenceTone,
  discountingLabel,
  decimalAsPct,
  horizonLabel,
  TV_RELIANCE_TOOLTIP,
  anchorTreatmentList,
  noFadeIsApproximate,
  NO_FADE_PRECISION_NOTE,
} from "../../../config/valuation";
import { fmtNum, fmtPct } from "../../../config/earningsNote";
import { fmtAsOf } from "../../../config/valuation";
import {
  NotePage, SectionTitle, Card, TileGrid, Banner, Pill, NoteSkeleton, NoteEmpty,
} from "../Widgets/earningsNoteUI";
import { DataAsOfBlock, StatementTable } from "../Widgets/valuationWidgets";
import { IntrinsicValueHistory } from "../Widgets/valuationHistory";
import ConsensusAnchorNote from "../Widgets/consensusAnchorNote";
import ScenarioBuilder from "../Widgets/scenarioBuilder";
import SensitivityGrid from "../Widgets/sensitivityGrid";
import FcfeTable from "../Widgets/fcfeTable";

/* Positive percentage = good. */
const upsideTone = (v) => (v == null ? "muted" : Number(v) >= 0 ? "pos" : "neg");

/**
 * `discount_rate_source` is the model name plus its raw inputs, e.g.
 *   "CAPM(rf=0.04650000000000001,beta=1.065,erp=0.0446)"
 * The floats carry binary representation noise and each one is already shown
 * elsewhere on the tab (risk-free rate and ERP in Data-as-of, beta in its own
 * tile), so only the model name is kept.
 */
const modelNameOnly = (src) => {
  if (!src) return null;
  const name = String(src).replace(/\s*\(.*\)\s*$/, "").trim();
  return name || null;
};

const IntrinsicValue = ({ stock }) => {
  const ticker = stock?.ticker;
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [state, setState] = useState({ error: false, notFound: false });
  /* The stored payload has no PV split, so the artifact's value-build card is
     filled from an empty-body /scenario call — the API defines that as
     "replay the stored result", and it is the same 15-min cached compute. */
  const [baseline, setBaseline] = useState(null);

  useEffect(() => {
    if (!ticker) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      setState({ error: false, notFound: false });
      setBaseline(null);
      const res = await getIntrinsicValue(ticker);
      if (cancelled) return;
      if (res?.success) setData(res.data);
      else setState({ error: !res?.notFound, notFound: !!res?.notFound });
      setLoading(false);

      if (!res?.success || !res.data?.intrinsic_value) return;
      const base = await computeIntrinsicValueScenario(ticker, {});
      if (cancelled) return;
      if (base?.success) setBaseline(base.data);
    })();
    return () => {
      cancelled = true;
    };
  }, [ticker]);

  if (loading) return <NoteSkeleton blocks={[110, 190, 260]} />;
  if (state.notFound) return <NoteEmpty>{ticker} is not in coverage.</NoteEmpty>;
  if (state.error || !data)
    return <NoteEmpty>Valuation data is not available for {ticker}.</NoteEmpty>;

  const {
    company_name,
    intrinsic_value: ivx,
    fcfe_history,
    sensitivity_grid,
    anchor_treatments,
    data_as_of,
    financials,
  } = data;

  const method = ivx?.calculation_method;
  const engine = engineMeta(method);

  /* ── Header cards — the artifact's five hero stats, in its order ── */
  const pricesAsOf = (data_as_of?.inputs ?? []).find((i) => i.name === "prices")?.as_of;

  const heroTiles = ivx
    ? [
        ivx.iv_per_share != null
          ? {
              label: "TV / share (today)",
              value: formatCurrency(ivx.iv_per_share),
              hint:
                ivx.equity_value != null && ivx.shares_used
                  ? `${formatCurrency(ivx.equity_value, { compact: true })} equity ÷ ${fmtL(
                      ivx.shares_used / 1e6,
                      0
                    )}M diluted shares${
                      data_as_of?.valuation_date
                        ? ` · discounted to ${fmtAsOf(data_as_of.valuation_date)}`
                        : ""
                    }${ivx.fy1_rem_frac != null ? " (FY+1 pro-rated)" : ""}`
                  : null,
            }
          : null,
        ivx.current_price != null
          ? {
              label: "Current price",
              value: formatCurrency(ivx.current_price),
              hint: pricesAsOf ? `prices as of ${fmtAsOf(pricesAsOf)}` : null,
            }
          : null,
        ivx.margin_of_safety_pct != null
          ? {
              label: "Margin of safety",
              value: fmtPct(ivx.margin_of_safety_pct, { decimals: 1 }),
              tone: upsideTone(ivx.margin_of_safety_pct),
            }
          : null,
        ivx.discount_rate != null
          ? {
              label: "Discount rate (CoE)",
              value: decimalAsPct(ivx.discount_rate, 1),
              hint: [
                ivx.terminal_growth_rate != null
                  ? `terminal g ${decimalAsPct(ivx.terminal_growth_rate, 1)}`
                  : null,
                discountingLabel(ivx.discounting),
                modelNameOnly(ivx.discount_rate_source),
              ]
                .filter(Boolean)
                .join(" · ") || null,
            }
          : null,
        ivx.tv_share_of_value != null
          ? {
              label: "Terminal-value reliance",
              value: decimalAsPct(ivx.tv_share_of_value, 0),
              tip: TV_RELIANCE_TOOLTIP,
              hint: horizonLabel(ivx.projection_years, ivx.continuation_years),
              tone: Number(ivx.tv_share_of_value) >= 0.75 ? "warn" : "neutral",
            }
          : null,
      ].filter(Boolean)
    : [];

  /* ── The artifact's right-hand value-build card ── */
  const buildTiles = ivx
    ? [
        (baseline?.equity_value ?? ivx.equity_value) != null
          ? {
              label: "Equity value",
              value: formatCurrency(baseline?.equity_value ?? ivx.equity_value, { compact: true }),
            }
          : null,
        baseline?.pv_projected != null
          ? {
              label: "PV of explicit FCFE",
              value: formatCurrency(baseline.pv_projected, { compact: true }),
              hint: ivx.projection_years ? `FY+1–${ivx.projection_years}` : null,
            }
          : null,
        baseline?.pv_terminal != null
          ? {
              label: ivx.continuation_years
                ? "PV of continuation + terminal"
                : "PV of terminal value",
              value: formatCurrency(baseline.pv_terminal, { compact: true }),
              hint: ivx.continuation_years ? `includes years 6–${5 + ivx.continuation_years}` : null,
            }
          : null,
      ].filter(Boolean)
    : [];

  /* ── Engine inputs the artifact shows in its notes rather than the hero ── */
  const engineTiles = ivx
    ? [
        ivx.upside_downside_pct != null
          ? {
              label: "Upside / downside",
              value: fmtPct(ivx.upside_downside_pct, { decimals: 1 }),
              tone: upsideTone(ivx.upside_downside_pct),
            }
          : null,
        ivx.tv_share_before_continuation != null
          ? {
              label: "Terminal-value share before continuation",
              value: decimalAsPct(ivx.tv_share_before_continuation, 0),
              hint:
                Number(ivx.tv_share_before_continuation) > 0.75
                  ? "above the 75% trigger — continuation years added"
                  : "below the 75% trigger — no continuation stage",
            }
          : null,
        /* "Growth rate used" (implied CAGR of the FCFE path) was removed
           2026-09-09 per Somya — the FCFE build below shows the path itself. */
        ivx.base_fcfe != null
          ? {
              label: "Base FCFE",
              value: formatCurrency(ivx.base_fcfe, { compact: true }),
              hint: "year-1 FCFE",
            }
          : null,
        /* Beta is a regression coefficient, NOT a rate — it renders as a plain
           ratio (0.10), never as a percentage. */
        ivx.beta != null ? { label: "Beta", value: fmtNum(ivx.beta, 2) } : null,
        isBankEngine(method) && ivx.cap_ratio != null
          ? { label: "Capital ratio", value: decimalAsPct(ivx.cap_ratio, 2), hint: "equity / assets" }
          : null,
        isBankEngine(method) && ivx.roe5 != null
          ? { label: "ROE (year 5)", value: decimalAsPct(ivx.roe5, 2) }
          : null,
        isAssetManagerEngine(method) && ivx.exit_multiple != null
          ? { label: "Exit multiple", value: `${fmtNum(ivx.exit_multiple, 2)}×`, hint: "P/E on terminal NI" }
          : null,
      ].filter(Boolean)
    : [];

  /* ── "TV / share by anchor treatment" ──
     All three treatments and their range arrive on this GET, so there is no
     second request. A null `anchor_treatments` means the name has no anchor to
     vary (asset-manager engine, unanchored/fallback rows) — the block hides. */
  const treatments = anchorTreatmentList(anchor_treatments);
  const treatmentRange = anchor_treatments?.range ?? null;
  const approxNoFade = noFadeIsApproximate(ivx?.consensus_anchor?.fade_den);

  return (
    <NotePage>
      {/* ── Masthead ── */}
      <div style={{ paddingTop: "var(--spacing-sm)" }}>
        <div
          style={{
            color: "var(--color-text-muted)",
            fontSize: "var(--font-size-xs)",
            fontWeight: "var(--font-weight-semibold)",
            textTransform: "uppercase",
            letterSpacing: "0.12em",
          }}
        >
          True value
        </div>
        <div
          style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap", marginTop: 2 }}
        >
          <span
            style={{
              color: "var(--color-text-primary)",
              fontSize: "var(--font-size-xl)",
              fontWeight: "var(--font-weight-bold)",
            }}
          >
            {company_name || stock?.company_name} ({data.ticker || ticker})
          </span>
          {ivx?.confidence && (
            <Pill tone={confidenceTone(ivx.confidence)}>{ivx.confidence} confidence</Pill>
          )}
        </div>
        <DataAsOfBlock dataAsOf={data_as_of} />
      </div>

      {/* ── No IV coverage ── */}
      {!ivx && (
        <Banner kind="plain" style={{ marginTop: "var(--spacing-md)" }}>
          The true-value engine has not covered {ticker} yet.
        </Banner>
      )}

      {/* ── Hero cards ── */}
      {heroTiles.length > 0 && (
        <>
          <SectionTitle
            caption={horizonLabel(ivx.projection_years, ivx.continuation_years) ?? undefined}
          >
            Valuation summary
          </SectionTitle>
          <TileGrid tiles={heroTiles} min={175} />
        </>
      )}

      {/* ── Value build (equity value / PV split) ── */}
      {buildTiles.length > 0 && (
        <div style={{ marginTop: "var(--spacing-sm)" }}>
          <TileGrid tiles={buildTiles} min={175} />
        </div>
      )}

      {/* ── TV / share by anchor treatment ── */}
      {treatments.length > 0 && (
        <Card style={{ marginTop: "var(--spacing-md)" }}>
          <div
            style={{
              color: "var(--color-text-muted)",
              fontSize: "var(--font-size-xs)",
              fontWeight: "var(--font-weight-semibold)",
              textTransform: "uppercase",
              letterSpacing: "0.03em",
            }}
          >
            TV / share by anchor treatment
            {treatmentRange?.low != null && treatmentRange?.high != null && (
              <>
                {" "}
                · range {formatCurrency(treatmentRange.low)} –{" "}
                {formatCurrency(treatmentRange.high)}
              </>
            )}
          </div>
          <div
            style={{
              display: "flex",
              flexWrap: "wrap",
              gap: "6px 20px",
              marginTop: 6,
              fontSize: "var(--font-size-sm)",
              color: "var(--color-text-secondary)",
            }}
          >
            {treatments.map((t) => (
              <span key={t.key} style={{ opacity: t.available ? 1 : 0.55 }}>
                {t.label}
                {t.key === "base" && (
                  <em style={{ color: "var(--color-text-muted)" }}> — base</em>
                )}
                :{" "}
                <strong style={{ color: "var(--color-text-primary)" }}>
                  {t.available ? formatCurrency(t.iv_per_share) : "not computable"}
                </strong>
                {t.key === "no_fade" && t.available && approxNoFade && (
                  <MuiTooltip title={NO_FADE_PRECISION_NOTE} placement="top" arrow>
                    <span style={{ cursor: "help", color: "var(--color-warning)" }}> ≈</span>
                  </MuiTooltip>
                )}
              </span>
            ))}
          </div>
          <div
            style={{
              marginTop: 6,
              color: "var(--color-text-muted)",
              fontSize: "var(--font-size-xs)",
              lineHeight: 1.6,
            }}
          >
            Base is the stored headline above. Pick a treatment in the Assumptions panel to
            recompute the rest of the model on it.
            {approxNoFade && <> {NO_FADE_PRECISION_NOTE}</>}
          </div>
        </Card>
      )}

      {/* ── Engine inputs ── */}
      {engineTiles.length > 0 && (
        <>
          <SectionTitle>Engine inputs</SectionTitle>
          <TileGrid tiles={engineTiles} min={170} />
        </>
      )}

      {/* ── Consensus-anchor note box ── */}
      {ivx?.consensus_anchor && <ConsensusAnchorNote anchor={ivx.consensus_anchor} />}

      {/* ── Sensitivity grid — placed directly after the anchor note ── */}
      <SensitivityGrid
        grid={sensitivity_grid}
        centreValue={ivx?.iv_per_share}
        caption="cost of equity × terminal growth at the stored assumptions · centre cell is the headline TV"
      />

      {/* ── Engine + internals ── */}
      {engine && (
        <Card style={{ marginTop: "var(--spacing-md)" }}>
          <div
            style={{
              display: "flex",
              alignItems: "baseline",
              gap: 10,
              flexWrap: "wrap",
              marginBottom: 6,
            }}
          >
            <span
              style={{
                color: "var(--color-text-primary)",
                fontSize: "var(--font-size-base)",
                fontWeight: "var(--font-weight-bold)",
              }}
            >
              {engine.label}
            </span>
            <Pill>{engine.who}</Pill>
          </div>
          <div
            style={{
              color: "var(--color-text-secondary)",
              fontSize: "var(--font-size-sm)",
              lineHeight: 1.7,
            }}
          >
            <div>
              <span style={{ opacity: 0.75 }}>FCFE definition: </span>
              {engine.fcfe}
            </div>
            <div>
              <span style={{ opacity: 0.75 }}>Terminal value: </span>
              {ivx?.terminal_formula || engine.terminal}
            </div>
            {ivx?.nb_method && (
              <div>
                <span style={{ opacity: 0.75 }}>Net borrowing projected by: </span>
                {nbMethodLabel(ivx.nb_method)}
              </div>
            )}
            {ivx?.fy1_rem_frac != null && (
              <div>
                <span style={{ opacity: 0.75 }}>First year pro-rated to: </span>
                {decimalAsPct(ivx.fy1_rem_frac, 2)} of the fiscal year remaining
              </div>
            )}
          </div>
        </Card>
      )}

      {/* ── Warnings ── */}
      {ivx && (ivx.warnings?.length > 0 || isFallbackMethod(method)) && (
        <div style={{ marginTop: "var(--spacing-md)", display: "grid", gap: "var(--spacing-sm)" }}>
          {isFallbackMethod(method) && (
            <Banner kind="warning">
              <strong>Fallback engine</strong> — the projection rows were unusable, so this name was
              valued by extrapolating trailing free cash flow. The terminal-growth tag,
              terminal-value reliance, continuation years and sensitivity grid may be unavailable.
            </Banner>
          )}
          {(ivx.warnings ?? []).map((w, i) => (
            <Banner key={i} kind="warning">
              {w}
            </Banner>
          ))}
        </div>
      )}

      {/* ── High terminal-value reliance ── */}
      {ivx?.tv_share_of_value != null && Number(ivx.tv_share_of_value) >= 0.75 && (
        <Banner kind="warning" style={{ marginTop: "var(--spacing-sm)" }}>
          <strong>{decimalAsPct(ivx.tv_share_of_value, 0)} of the value sits in the terminal
          value</strong> — the result is highly sensitive to the terminal growth and discount rate.
          The grid above shows how much.
        </Banner>
      )}

      {/* ── FCFE, historical then projected, as one series ── */}
      <FcfeTable
        history={fcfe_history ?? []}
        projected={ivx?.projected_fcfe ?? []}
        method={method}
      />

      {/* ── Assumptions panel + save-version bar + Excel export ── */}
      {ivx && (
        <ScenarioBuilder
          ticker={ticker}
          intrinsicValue={ivx}
          dataAsOf={data_as_of}
          anchorTreatments={anchor_treatments}
        />
      )}

      {/* ── Value over time ── */}
      <IntrinsicValueHistory ticker={ticker} />

      {/* ── Statements ── */}
      {financials && (
        <>
          <SectionTitle caption="actuals and projected fiscal years — projected columns flagged">
            Financial statements
          </SectionTitle>
          <div style={{ display: "grid", gap: "var(--spacing-sm)" }}>
            {STATEMENTS.map((st, i) => (
              <StatementTable
                key={st.key}
                statement={st}
                periods={financials[st.key]}
                defaultOpen={i === 0}
              />
            ))}
          </div>
        </>
      )}
    </NotePage>
  );
};

export default IntrinsicValue;
